'use client';

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import type { SingleValue } from 'react-select';
import type { RootState, AppDispatch } from '@/lib/store';
import { createTestimonial } from '@/lib/store/slices/testimonialSlice';
import { fetchActiveProjects } from '@/lib/store/slices/projectSlice';
import { useTranslations } from 'next-intl';
import { validateRateUs, type RateUsFormErrors } from '@/lib/validations/rateUsSchema';
import { sanitizeObject } from '@/lib/utils/sanitize';
import { checkRateLimit } from '@/lib/utils/rateLimit';

interface FeedbackClientProps { locale: string }

interface SelectOption { value: string; label: string }

interface FormState {
    name: string;
    email: string;
    company: string;
    position: string;
    commentEn: string;
    commentAr: string;
    isAnonymous: boolean;
}

const initial: FormState = {
    name: '', email: '', company: '', position: '',
    commentEn: '', commentAr: '', isAnonymous: false,
};

// ── Star Rating ───────────────────────────────────────────────────────────────

function StarRating({
    value,
    hover,
    onChange,
    onHover,
    onLeave,
    error,
    label,
}: {
    value: number;
    hover: number;
    onChange: (v: number) => void;
    onHover: (v: number) => void;
    onLeave: () => void;
    error?: string;
    label: string;
}) {
    const active = hover || value;
    return (
        <div>
            <p className="text-xs font-medium text-(--muted) mb-2">{label} *</p>
            <div
                className="flex gap-1.5"
                role="radiogroup"
                aria-label={label}
                aria-required="true"
            >
                {[1, 2, 3, 4, 5].map((star) => (
                    <button
                        key={star}
                        type="button"
                        role="radio"
                        aria-checked={value === star}
                        aria-label={`${star} star${star !== 1 ? 's' : ''}`}
                        onClick={() => onChange(star)}
                        onMouseEnter={() => onHover(star)}
                        onMouseLeave={onLeave}
                        onFocus={() => onHover(star)}
                        onBlur={onLeave}
                        className="focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
                    >
                        <svg
                            className={`w-9 h-9 transition-colors duration-100 ${
                                star <= active
                                    ? 'text-primary-400 fill-primary-400'
                                    : 'text-(--border) fill-(--border)'
                            }`}
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                        >
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                    </button>
                ))}
            </div>
            {error && <p role="alert" className="mt-1 text-xs text-danger-500">{error}</p>}
        </div>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function FeedbackClient({ locale }: FeedbackClientProps) {
    const t = useTranslations('RateUs');
    const dispatch = useDispatch<AppDispatch>();
    const { loading } = useSelector((s: RootState) => s.testimonials);
    const { items: projects } = useSelector((s: RootState) => s.projects);

    const lang  = locale === 'ar' ? 'ar' : 'en';
    const isRtl = lang === 'ar';

    useEffect(() => { dispatch(fetchActiveProjects({limit: 1000})); }, [dispatch]);

    const [form, setForm]           = useState<FormState>(initial);
    const [rating, setRating]       = useState(0);
    const [hoverRating, setHover]   = useState(0);
    const [errors, setErrors]       = useState<RateUsFormErrors>({});
    const [status, setStatus]       = useState<'idle' | 'success' | 'error' | 'rate-limited'>('idle');
    const [selectedProject, setSelectedProject] = useState<SingleValue<SelectOption>>(null);

    const projectOptions = useMemo<SelectOption[]>(() =>
        projects.map(p => ({
            value: p._id,
            label: lang === 'ar' ? p.title.ar : p.title.en,
        })),
    [projects, lang]);

    const set = (field: keyof FormState) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const value = field === 'isAnonymous'
            ? (e.target as HTMLInputElement).checked
            : e.target.value;
        setForm(prev => ({ ...prev, [field]: value }));
        setErrors(prev => ({ ...prev, [field]: undefined }));
    };

    const validate = (): boolean => {
        const result = validateRateUs({ ...form, rating });
        if (result.success) { setErrors({}); return true; }
        setErrors(result.errors);
        return false;
    };

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        if (!checkRateLimit('feedback-form', 2, 60_000)) {
            setStatus('rate-limited');
            return;
        }

        const safe = sanitizeObject({ ...form });
        const commentEn = safe.commentEn.trim();
        const commentAr = safe.commentAr.trim() || commentEn;

        try {
            await dispatch(createTestimonial({
                name:        safe.name,
                email:       safe.email || undefined,
                company:     safe.company ? { ar: safe.company, en: safe.company } : undefined,
                position:    safe.position ? { ar: safe.position, en: safe.position } : undefined,
                rating,
                comment:     { ar: commentAr, en: commentEn },
                projectRef:  selectedProject?.value || undefined,
                isAnonymous: form.isAnonymous,
            } as any)).unwrap();

            setStatus('success');
            setForm(initial);
            setRating(0);
            setSelectedProject(null);
        } catch {
            setStatus('error');
        }
    }, [dispatch, form, rating, selectedProject]);

    const inputClass = (field: keyof RateUsFormErrors) =>
        `w-full rounded-xl border px-4 py-3 text-sm bg-(--surface) text-(--text) placeholder:text-(--muted)
         focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500
         transition-colors duration-180
         ${errors[field] ? 'border-danger-500' : 'border-(--border)'}`;

    const selectStyles = {
        control: (base: any, state: any) => ({
            ...base,
            backgroundColor: 'var(--surface)',
            borderColor: state.isFocused ? 'var(--color-primary-500)' : 'var(--border)',
            borderRadius: '0.75rem',
            padding: '4px 4px',
            fontSize: '0.875rem',
            boxShadow: state.isFocused ? '0 0 0 2px rgb(249 115 22 / 0.3)' : 'none',
            '&:hover': { borderColor: 'var(--border)' },
        }),
        menu: (base: any) => ({
            ...base,
            backgroundColor: 'var(--surface)',
            borderRadius: '0.75rem',
            border: '1px solid var(--border)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            overflow: 'hidden',
            zIndex: 50,
        }),
        menuList: (base: any) => ({ ...base, padding: '4px' }),
        option: (base: any, state: any) => ({
            ...base,
            borderRadius: '0.5rem',
            backgroundColor: state.isSelected
                ? 'var(--color-primary-500)'
                : state.isFocused ? 'var(--bg)' : 'transparent',
            color: state.isSelected ? 'white' : 'var(--text)',
            fontSize: '0.875rem',
            cursor: 'pointer',
        }),
        singleValue:        (base: any) => ({ ...base, color: 'var(--text)' }),
        placeholder:        (base: any) => ({ ...base, color: 'var(--muted)', fontSize: '0.875rem' }),
        input:              (base: any) => ({ ...base, color: 'var(--text)' }),
        indicatorSeparator: () => ({ display: 'none' }),
        dropdownIndicator:  (base: any) => ({ ...base, color: 'var(--muted)', padding: '4px' }),
        clearIndicator:     (base: any) => ({ ...base, color: 'var(--muted)', padding: '4px' }),
        noOptionsMessage:   (base: any) => ({ ...base, color: 'var(--muted)', fontSize: '0.875rem' }),
    };

    const ratingLabels: Record<number, string> = {
        1: t('stars.one'),
        2: t('stars.two'),
        3: t('stars.three'),
        4: t('stars.four'),
        5: t('stars.five'),
    };

    const activeRating = hoverRating || rating;

    return (
        <section className="section-padding px-4 sm:px-6">
            <div className="max-w-5xl mx-auto grid lg:grid-cols-5 gap-10 lg:gap-16">

                {/* Info panel */}
                <aside className="lg:col-span-2 space-y-5">
                    {/* Why rate card */}
                    <div className="rounded-2xl border border-(--border) bg-(--surface) p-6 space-y-4">
                        <p className="text-xs font-semibold text-(--muted) uppercase tracking-wider">{t('info.whyTitle')}</p>
                        <ul className="space-y-3">
                            {[t('info.reason1'), t('info.reason2'), t('info.reason3')].map((r, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <div className="w-6 h-6 rounded-full bg-primary-500/10 flex items-center justify-center shrink-0 mt-0.5">
                                        <svg className="w-3.5 h-3.5 text-primary-500" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <p className="text-sm text-(--text)">{r}</p>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Privacy note */}
                    <div className="rounded-2xl border border-(--border) bg-(--surface) p-6 space-y-3">
                        <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-primary-500 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                            <p className="text-xs font-semibold text-(--muted) uppercase tracking-wider">{t('info.privacyTitle')}</p>
                        </div>
                        <p className="text-sm text-(--muted)">{t('info.privacyNote')}</p>
                    </div>

                    {/* Approval note */}
                    <div className="rounded-2xl border border-(--border) bg-(--surface) p-6">
                        <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-lg bg-warning-400/10 flex items-center justify-center shrink-0 mt-0.5">
                                <svg className="w-4 h-4 text-warning-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <p className="text-sm text-(--muted)">{t('info.approvalNote')}</p>
                        </div>
                    </div>
                </aside>

                {/* Form */}
                <div className="lg:col-span-3">
                    <div aria-live="assertive" aria-atomic="true" className="sr-only">
                        {status === 'success' && t('success')}
                        {status === 'error'   && t('error')}
                        {status === 'rate-limited' && t('rateLimited')}
                        {Object.keys(errors).length > 0 &&
                            `${Object.keys(errors).length} error${Object.keys(errors).length > 1 ? 's' : ''} in the form.`
                        }
                    </div>

                    {status === 'success' ? (
                        <div
                            role="status"
                            aria-live="polite"
                            className="rounded-2xl border border-success-500/30 bg-success-500/5 p-10 text-center space-y-4"
                        >
                            <div className="w-16 h-16 rounded-full bg-success-500/10 flex items-center justify-center mx-auto">
                                <svg className="w-8 h-8 text-success-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <p className="text-lg font-semibold text-(--text)">{t('success')}</p>
                            <p className="text-sm text-(--muted)">{t('successNote')}</p>
                            <button
                                onClick={() => setStatus('idle')}
                                className="text-sm text-primary-500 hover:underline cursor-pointer"
                            >
                                {t('submitAnother')}
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} noValidate className="space-y-5" aria-label={t('heading')}>

                            {/* Star rating */}
                            <div className="rounded-2xl border border-(--border) bg-(--surface) p-5 space-y-3">
                                <StarRating
                                    value={rating}
                                    hover={hoverRating}
                                    onChange={(v) => { setRating(v); setErrors(prev => ({ ...prev, rating: undefined })); }}
                                    onHover={setHover}
                                    onLeave={() => setHover(0)}
                                    error={errors.rating}
                                    label={t('rating')}
                                />
                                {activeRating > 0 && (
                                    <p className="text-sm font-medium text-primary-500 transition-opacity">
                                        {ratingLabels[activeRating]}
                                    </p>
                                )}
                            </div>

                            {/* Name + Email */}
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-(--muted) mb-1.5" htmlFor="fb-name">{t('name')} *</label>
                                    <input
                                        id="fb-name" type="text" value={form.name} onChange={set('name')}
                                        placeholder={t('name')}
                                        aria-invalid={!!errors.name} aria-describedby={errors.name ? 'fb-name-err' : undefined}
                                        aria-required="true" className={inputClass('name')}
                                    />
                                    {errors.name && <p id="fb-name-err" role="alert" className="mt-1 text-xs text-danger-500">{errors.name}</p>}
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-(--muted) mb-1.5" htmlFor="fb-email">{t('email')}</label>
                                    <input
                                        id="fb-email" type="email" value={form.email} onChange={set('email')}
                                        placeholder={t('email')}
                                        aria-invalid={!!errors.email} aria-describedby={errors.email ? 'fb-email-err' : undefined}
                                        className={inputClass('email')}
                                    />
                                    {errors.email && <p id="fb-email-err" role="alert" className="mt-1 text-xs text-danger-500">{errors.email}</p>}
                                </div>
                            </div>

                            {/* Company + Position */}
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-(--muted) mb-1.5" htmlFor="fb-company">{t('company')}</label>
                                    <input
                                        id="fb-company" type="text" value={form.company} onChange={set('company')}
                                        placeholder={t('company')} className={inputClass('company')}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-(--muted) mb-1.5" htmlFor="fb-position">{t('position')}</label>
                                    <input
                                        id="fb-position" type="text" value={form.position} onChange={set('position')}
                                        placeholder={t('position')} className={inputClass('position')}
                                    />
                                </div>
                            </div>

                            {/* Review (English) */}
                            <div>
                                <label className="block text-xs font-medium text-(--muted) mb-1.5" htmlFor="fb-comment-en">
                                    {t('commentEn')} *
                                </label>
                                <textarea
                                    id="fb-comment-en"
                                    rows={4}
                                    value={form.commentEn}
                                    onChange={set('commentEn')}
                                    placeholder={t('commentEnPlaceholder')}
                                    dir="ltr"
                                    aria-invalid={!!errors.commentEn}
                                    aria-describedby={errors.commentEn ? 'fb-comment-en-err' : undefined}
                                    aria-required="true"
                                    className={`${inputClass('commentEn')} resize-none`}
                                />
                                {errors.commentEn && <p id="fb-comment-en-err" role="alert" className="mt-1 text-xs text-danger-500">{errors.commentEn}</p>}
                            </div>

                            {/* Review (Arabic) */}
                            <div>
                                <label className="block text-xs font-medium text-(--muted) mb-1.5" htmlFor="fb-comment-ar">
                                    {t('commentAr')}
                                    <span className="text-(--muted) font-normal ms-1">({t('optional')})</span>
                                </label>
                                <textarea
                                    id="fb-comment-ar"
                                    rows={4}
                                    value={form.commentAr}
                                    onChange={set('commentAr')}
                                    placeholder={t('commentArPlaceholder')}
                                    dir="rtl"
                                    className={`${inputClass('commentAr')} resize-none`}
                                />
                                <p className="mt-1 text-xs text-(--muted)">{t('commentArHint')}</p>
                            </div>

                            {/* Project reference */}
                            <div>
                                <label id="fb-project-label" className="block text-xs font-medium text-(--muted) mb-1.5">
                                    {t('project')}
                                    <span className="text-(--muted) font-normal ms-1">({t('optional')})</span>
                                </label>
                                <Select<SelectOption>
                                    instanceId="fb-project"
                                    inputId="fb-project-input"
                                    aria-labelledby="fb-project-label"
                                    options={projectOptions}
                                    value={selectedProject}
                                    onChange={setSelectedProject}
                                    isClearable
                                    isSearchable
                                    placeholder={t('projectPlaceholder')}
                                    noOptionsMessage={() => t('noProjects')}
                                    styles={selectStyles}
                                    isRtl={isRtl}
                                />
                            </div>

                            {/* Anonymous checkbox */}
                            <label className="flex items-center gap-3 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    checked={form.isAnonymous}
                                    onChange={set('isAnonymous')}
                                    className="w-4 h-4 text-primary-600 border-(--border) rounded accent-primary-500"
                                />
                                <span className="text-sm text-(--text) group-hover:text-primary-500 transition-colors">
                                    {t('anonymous')}
                                </span>
                            </label>

                            {/* Error / rate limit banners */}
                            {status === 'error' && (
                                <p role="alert" className="text-sm text-danger-500">{t('error')}</p>
                            )}
                            {status === 'rate-limited' && (
                                <p role="alert" className="text-sm text-danger-500">{t('rateLimited')}</p>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                aria-busy={loading}
                                className="
                                    w-full py-3 rounded-xl bg-primary-500 text-neutral-900 font-semibold text-sm
                                    hover:bg-primary-600 active:bg-primary-700
                                    disabled:opacity-50 disabled:cursor-not-allowed
                                    transition-colors duration-180 cursor-pointer
                                    focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-2
                                "
                            >
                                {loading ? t('submitting') : t('submit')}
                            </button>

                        </form>
                    )}
                </div>
            </div>
        </section>
    );
}
